import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import api from '@/api/axiosConfig.js';
import { fetchReporteOperacion } from '@/services/reportes.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { hasPermission } from '@/utils/permissions.js';
import { MONITOR_REPARTOS } from '@/graphql/repartos.gql.js';
import { ALERTAS_DASHBOARD, RESOLVER_ALERTA } from '@/graphql/trazabilidadAlertas.gql.js';

const AUTO_REFRESH_MS = 30000;

const formatDateTime = value => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('es-AR', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(value);
};

export default function MonitorOperaciones() {
  const { sucursal, user } = useAuth();
  const sucursalId = Number(sucursal?.id || 0);
  const searchRef = useRef(null);

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [guiasPorEstado, setGuiasPorEstado] = useState([]);
  const [fojasResumen, setFojasResumen] = useState({ abiertas: 0, cerradas: 0 });

  const [{ data: repartosData }, reexecuteRepartos] = useQuery({
    query: MONITOR_REPARTOS,
    variables: { sucursalId },
    pause: !sucursalId,
    requestPolicy: 'network-only',
  });

  const [{ data: alertasData }, reexecuteAlertas] = useQuery({
    query: ALERTAS_DASHBOARD,
    variables: { sucursal_id: sucursalId, limite: 100 },
    pause: !sucursalId,
    requestPolicy: 'network-only',
  });


  const [, resolverAlerta] = useMutation(RESOLVER_ALERTA);
  const canResolverAlertas = hasPermission(user, 'FOJA_RECIBIR') || hasPermission(user, 'gerencial.roles.editar');

  const loadMonitor = useCallback(async (isManual = false) => {
    if (!sucursalId) return;
    setError('');
    setRefreshing(isManual);
    setLoading(!isManual);
    try {
      const [operacion, fojasResponse] = await Promise.all([
        fetchReporteOperacion({ sucursalId }),
        api.get('/api/fojas', { params: { sucursal_id: sucursalId, limit: 300 } }).catch(() => ({ data: {} })),
      ]);

      const rows = Array.isArray(operacion?.guias_por_estado) ? operacion.guias_por_estado : [];
      setGuiasPorEstado(rows);

      const fojasRows = fojasResponse?.data?.data || fojasResponse?.data?.rows || [];
      const normalized = Array.isArray(fojasRows) ? fojasRows : [];
      const resumen = normalized.reduce(
        (acc, item) => {
          const estado = String(item?.estado || '').toUpperCase();
          if (estado.includes('CERR')) {
            acc.cerradas += 1;
          } else {
            acc.abiertas += 1;
          }
          return acc;
        },
        { abiertas: 0, cerradas: 0 }
      );
      setFojasResumen(resumen);
      setLastUpdated(new Date());
      reexecuteRepartos({ requestPolicy: 'network-only' });
      reexecuteAlertas({ requestPolicy: 'network-only' });
    } catch (err) {
      setError(err?.message || 'No se pudo cargar el monitor.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [reexecuteAlertas, reexecuteRepartos, sucursalId]);

  useEffect(() => {
    loadMonitor(false);
  }, [loadMonitor]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      loadMonitor(false);
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [loadMonitor]);

  useEffect(() => {
    const onKeyDown = event => {
      if (event.key.toLowerCase() === 'r' && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        loadMonitor(true);
      }
      if (event.key === '/') {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [loadMonitor]);

  const filteredGuias = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return guiasPorEstado;
    return guiasPorEstado.filter(item => String(item.estado || '').toLowerCase().includes(term));
  }, [guiasPorEstado, query]);

  const filteredAlertas = useMemo(() => {
    const term = query.trim().toLowerCase();
    const rows = alertasData?.alertasDashboard || [];
    if (!term) return rows;
    return rows.filter(
      item =>
        String(item.tipo || '').toLowerCase().includes(term)
        || String(item.mensaje || '').toLowerCase().includes(term)
        || String(item.referencia_tipo || '').toLowerCase().includes(term)
        || String(item.referencia_id || '').toLowerCase().includes(term)
    );
  }, [alertasData, query]);

  const repartos = repartosData?.monitorRepartos || {
    abiertos: 0,
    en_rendicion: 0,
    liquidados: 0,
  };

  const resolverConMotivo = async (item, snoozeHoras = null) => {
    if (!canResolverAlertas || !item?.id) return;
    const motivo = window.prompt(
      snoozeHoras ? `Motivo para posponer ${snoozeHoras}h` : 'Motivo de resolución',
      ''
    );
    if (motivo === null) return;
    await resolverAlerta({
      input: {
        id: Number(item.id),
        motivo: String(motivo || '').trim() || null,
        usuario: String(user?.id || user?.user_id || 'web'),
        snooze_horas: snoozeHoras,
      },
    });
    reexecuteAlertas({ requestPolicy: 'network-only' });
  };


  return (
    <div className="space-y-4 py-4">
      <header className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Monitor de operaciones</h1>
            <p className="text-sm text-[rgb(var(--muted))]">Vista operativa full width con actualización automática cada 30s.</p>
          </div>
          <button
            type="button"
            onClick={() => loadMonitor(true)}
            className="rounded-xl border border-[rgb(var(--border))] px-3 py-2 text-sm"
          >
            {refreshing ? 'Actualizando…' : 'Actualizar (R)'}
          </button>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <input
            ref={searchRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Buscar estado, alerta o referencia… (/)"
            className="rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--subpanel))] px-3 py-2"
          />
          <p className="self-center text-sm text-[rgb(var(--muted))]">
            Última actualización: <span className="font-medium text-[rgb(var(--fg))]">{formatDateTime(lastUpdated)}</span>
          </p>
        </div>
        {error ? <p className="mt-3 text-sm text-red-400">{error}</p> : null}
      </header>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <article className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] p-4">
          <h2 className="mb-2 text-base font-semibold">Guías por estado</h2>
          {loading ? <p className="text-sm text-[rgb(var(--muted))]">Cargando…</p> : null}
          <ul className="space-y-1 text-sm">
            {filteredGuias.map(item => (
              <li key={item.estado} className="flex items-center justify-between">
                <span>{item.estado}</span>
                <strong>{item.cantidad}</strong>
              </li>
            ))}
            {!filteredGuias.length ? <li className="text-[rgb(var(--muted))]">Sin datos</li> : null}
          </ul>
        </article>

        <article className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] p-4">
          <h2 className="mb-2 text-base font-semibold">Fojas abiertas/cerradas</h2>
          <div className="space-y-2 text-sm">
            <p className="flex justify-between"><span>Abiertas</span><strong>{fojasResumen.abiertas}</strong></p>
            <p className="flex justify-between"><span>Cerradas</span><strong>{fojasResumen.cerradas}</strong></p>
          </div>
        </article>

        <article className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] p-4">
          <h2 className="mb-2 text-base font-semibold">Repartos activos</h2>
          <div className="space-y-2 text-sm">
            <p className="flex justify-between"><span>Abiertos</span><strong>{repartos.abiertos || 0}</strong></p>
            <p className="flex justify-between"><span>En rendición</span><strong>{repartos.en_rendicion || 0}</strong></p>
            <p className="flex justify-between"><span>Liquidados</span><strong>{repartos.liquidados || 0}</strong></p>
          </div>
        </article>

        <article className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] p-4">
          <h2 className="mb-2 text-base font-semibold">Alertas</h2>
          <p className="text-3xl font-bold leading-none">{filteredAlertas.length}</p>
          <p className="mt-1 text-xs text-[rgb(var(--muted))]">Mostrando según filtro actual.</p>
        </article>
      </section>

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] p-4">
        <h3 className="mb-3 text-base font-semibold">Detalle de alertas</h3>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-[rgb(var(--border))] text-left">
                <th className="pb-2 pr-3">Severidad</th>
                <th className="pb-2 pr-3">Tipo</th>
                <th className="pb-2 pr-3">Referencia</th>
                <th className="pb-2 pr-3">Mensaje</th>
                <th className="pb-2 pr-3">Acciones rápidas</th>
              </tr>
            </thead>
            <tbody>
              {filteredAlertas.map(item => (
                <tr key={item.id || `${item.tipo}-${item.referencia_id}`} className="border-b border-[rgb(var(--border))]">
                  <td className="py-2 pr-3">{item.severidad}</td>
                  <td className="py-2 pr-3">{item.tipo}</td>
                  <td className="py-2 pr-3">{item.referencia_tipo} #{item.referencia_id}</td>
                  <td className="py-2 pr-3">
                    <div className="text-xs text-[rgb(var(--muted))]">{formatDateTime(item.created_at)}</div>
                    <div>{item.mensaje}</div>
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex flex-wrap gap-2">
                      {item.referencia_tipo === 'GUIA' ? (
                        <a className="rounded border border-[rgb(var(--border))] px-2 py-1" href={`/guias/${item.referencia_id}`}>Abrir guía</a>
                      ) : null}
                      {canResolverAlertas && item.id ? (
                        <>
                          <button type="button" className="rounded border border-[rgb(var(--border))] px-2 py-1" onClick={() => resolverConMotivo(item, null)}>Marcar recibida</button>
                          <button type="button" className="rounded border border-[rgb(var(--border))] px-2 py-1" onClick={() => resolverConMotivo(item, 4)}>Snooze 4h</button>
                          <button type="button" className="rounded border border-[rgb(var(--border))] px-2 py-1" onClick={() => resolverConMotivo(item, 24)}>Snooze 24h</button>
                        </>
                      ) : (
                        <span className="text-xs text-[rgb(var(--muted))]">Sin permisos</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!filteredAlertas.length ? (
                <tr>
                  <td className="py-3 text-[rgb(var(--muted))]" colSpan={5}>Sin alertas para el filtro actual.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

